import axios from '@/axiosInterceptor';
import uniqBy from 'lodash.uniqby';
import { setGrafanaFaroUser } from '@/services/grafana';

const state = () => ({
  currentUser: null,
  companyUsers: null,
  companyUsersPagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  preferredCountry: 'us',
  intercomIsBooted: false,
  intercomIsUserLoggedIn: false,
  featureFlags: {},
  inquiries: null,
  verifiedUsers: null,
  userLoadBoardAccess: {},
  isCompanyUsersLoading: false,
});

// getters
const getters = {
  getFeatureFlag: (state) => (key) => {
    const feature = state.featureFlags[key];
    return feature ? feature : null;
  },
  isInquiryApproved: (state) => {
    return state.inquiries?.[0]?.status === 'approved';
  },
  getLatestInquiry: (state) => {
    return state.inquiries?.length > 0 ? state.inquiries[0] : state.inquiries;
  },
  inquiryStatus: (state) => {
    if (state.currentUser?.last_verified_identity_at) {
      return 'approved';
    }

    return state.inquiries?.length > 0
      ? state.inquiries[0].status
      : state.inquiries;
  },
  userIsADriver: (state) => {
    return (
      state.currentUser?.scopes?.length === 1 &&
      state.currentUser?.scopes?.includes('driver')
    );
  },
  hasConsentedToLegalAgreements: (state) => {
    const agreements = state.currentUser?.user_agreements_per_type || {};
    const carrierEntries =
      Object.entries(agreements)?.filter(([k]) => k?.startsWith('carrier_')) ||
      [];

    if (carrierEntries?.length === 0) return true;

    return carrierEntries?.every(([, v]) => v === true);
  },
};

// actions
const actions = {
  // for a broker user who has not requested to be a carrier yet
  async setRequestedCarrierId({ commit }, { requested_carrier_id, user_id }) {
    let body = {
      user: {
        requested_carrier_id: requested_carrier_id,
      },
    };
    try {
      let res = await axios.patch(
        process.env.API_IDENTITY + '/api/v1/users/' + user_id,
        body
      );
      commit('setRequestedCarrier', requested_carrier_id);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  // Refresh the state of the current user; check
  // first if in state and if not then go get
  // from the server
  async refreshCurrentUser({ dispatch, state }) {
    if (state.currentUser?.id > 0) {
      return state.currentUser;
    } else if (state.currentUser?.company_verification_status === 'locked') {
      // bypass the server call if the user is locked
      console.log(
        'User is locked. Waiting on confirmation from the account owner to add you to their account.'
      );
    } else {
      return await dispatch('serverRefreshCurrentUser');
    }
  },

  async refreshCurrentFeatureFlags({ dispatch, state }, user) {
    if (Object.keys(state.featureFlags)?.length > 0) {
      return state.featureFlags;
    } else {
      await dispatch('getFeatureFlags', user);
    }
  },

  async refreshCurrentLoadBoardAccess({ dispatch, state }) {
    if (Object.keys(state.userLoadBoardAccess)?.length > 0) {
      return state.userLoadBoardAccess;
    } else {
      return await dispatch('checkLoadBoardAccess');
    }
  },

  // Refresh the user from the server using the identify
  // endpoint
  async serverRefreshCurrentUser({ dispatch, commit }) {
    try {
      const res = await axios.get(process.env.API_IDENTITY + '/refresh_jwt');
      commit('setCurrentUser', res.data);
      dispatch('setGTMVars', res.data);
      if (res.data?.requested_carrier?.is_email_required_for_login) {
        commit(
          'carriers/setEmailRequiredForLogin',
          res.data?.requested_carrier?.is_email_required_for_login,
          { root: true }
        );
      }
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [401].includes(e?.response?.status)) {
        commit('setCurrentUser', null);
        return e.response;
        // The 406 comes from a VPN block and the 422 when they are waiting
        // to be added to someone's account.
      } else if (
        axios.isAxiosError(e) &&
        [406, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async identifyUser({ dispatch, commit }) {
    try {
      const res = await axios.get(process.env.API_IDENTITY + '/identify');
      commit('setCurrentUser', res.data);
      dispatch('setGTMVars', res.data);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [401].includes(e?.response?.status)) {
        commit('setCurrentUser', null);
        return e.response;
      } else {
        throw e;
      }
    }
  },
  // Refresh the JWT cookie after the user
  // has signed up
  // async serverRefreshJwt({ dispatch, commit }) {
  //   try {
  //     const res = await axios.get(process.env.API_IDENTITY + "/refresh_jwt");
  //     commit("setCurrentUser", res.data);
  //     dispatch('setGTMVars', res.data);
  //     if (res.data?.requested_carrier?.is_email_required_for_login) {
  //       commit(
  //         "carriers/setEmailRequiredForLogin",
  //         res.data?.requested_carrier?.is_email_required_for_login,
  //         { root: true }
  //       );
  //     }
  //     return res;
  //   } catch (e) {
  //     if (axios.isAxiosError(e) && [401].includes(e?.response?.status)) {
  //       commit("setCurrentUser", null);
  //       return e.response;
  //     } else {
  //       throw e;
  //     }
  //   }
  // },
  setGTMVars(_, user) {
    if (process.env.NODE_ENV !== 'development') {
      /* eslint-disable-next-line */
      dataLayer.push({
        user_id: user?.id || null,
        company_id: user?.company?.id || null,
        company_name: user?.company?.legal_name || null,
        company_type: user?.company?.company_type || null,
        company_plan_id: user?.company?.plan_id || null,
        company_status: user?.company?.status || null,
        requested_carrier_id: user?.requested_carrier_id || null,
        carrier_id: user?.company?.carrier_id || null,
        user_can_api: user?.paywall?.can_api || null,
        user_can_api_with_insights:
          user?.paywall?.can_api_with_insights || null,
        user_can_connect: user?.paywall?.can_connect || null,
        user_can_export: user?.paywall?.can_export || null,
        user_can_export_with_insights:
          user?.paywall?.can_export_with_insights || null,
        user_can_view_insights: user?.paywall?.can_view_insights || null,
        user_can_view_insurance_certificates:
          user?.paywall?.can_view_insurance_certificates || null,
        user_scope_manage_users: user?.scopes?.includes('manage_users') || null,
        user_scope_sourcing: user?.scopes?.includes('sourcing') || null,
        user_scope_manage_rule_overrides:
          user?.scopes?.includes('manage_rule_overrides') || null,
        user_scope_onboarding: user?.scopes?.includes('onboarding') || null,
        user_scope_surveys_publish:
          user?.scopes?.includes('surveys_publish') || null,
        user_scope_surveys_workflow:
          user?.scopes?.includes('surveys_workflow') || null,
        user_scope_insurance_workflow:
          user?.scopes?.includes('insurance_workflow') || null,
        user_scope_support_workflow:
          user?.scopes?.includes('support_workflow') || null,
        user_scope_view_bank_accounts:
          user?.scopes?.includes('view_bank_accounts') || null,
        user_scope_connection_notifications:
          user?.scopes?.includes('connection_notifications') || null,
      });
    }
  },
  // // Create a verification for logging in
  // async postSignIn(_, body) {
  //   try {
  //     const res = await axios.post(process.env.API_IDENTITY + "/sign_in", body);
  //     return res;
  //   } catch (e) {
  //     if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
  //       return e.response;
  //     } else {
  //       throw e;
  //     }
  //   }
  // },

  // // Send the sign in code
  // async postSignInCode({ commit }, body) {
  //   try {
  //     const res = await axios.post(
  //       process.env.API_IDENTITY + "/sign_in/code",
  //       body
  //     );
  //     commit("setCurrentUser", res.data);
  //     return res;
  //   } catch (e) {
  //     if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
  //       return e.response;
  //     } else {
  //       throw e;
  //     }
  //   }
  // },
  // Send the sign in code
  async updatePhoneWithVerification({ commit }, verification_id) {
    try {
      const res = await axios.patch(
        process.env.API_IDENTITY +
          '/api/v1/users/update_phone_with_verification',
        { verification_id: verification_id }
      );
      // Don't set current user if qr_code is returned (TOTP setup required)
      if (!res.data?.qr_code) {
        commit('setCurrentUser', res.data);
      }
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async postSignUpOrLoginManual(_, body) {
    try {
      const res = await axios.post(
        process.env.API_IDENTITY +
          '/sign_up/manually_created_carrier_verification',
        body
      );
      // const res = { status: 200 }
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 409, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async postSignUpOrLoginManualCode(_, body) {
    try {
      const res = await axios.post(
        process.env.API_IDENTITY +
          '/sign_up/manually_created_carrier_verification/code',
        body
      );
      // const res = { status: 200 }
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 409, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  // Create a verification for signing up with phone number
  async postSignUpOrLogin(_, body) {
    try {
      const res = await axios.post(
        process.env.API_IDENTITY + '/carrier_sign_in_or_sign_up',
        body
      );
      // const res = { status: 200 }
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 422, 409].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  // Check that the verification code is correct with the current user
  async postSignUpOrLoginCode({ commit }, body) {
    try {
      const res = await axios.post(
        process.env.API_IDENTITY + '/carrier_validate_code',
        body
      );

      commit('setCurrentUser', res.data);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async patchSignUpDetails({ commit }, body) {
    try {
      const res = await axios.patch(
        process.env.API_IDENTITY + '/sign_up/details',
        body
      );
      commit('setCurrentUser', res.data);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async postCreateCompany(_, body) {
    try {
      return await axios.post(
        process.env.API_IDENTITY + '/sign_up/create_company',
        body
      );
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async patchCheckOpenInvitation() {
    try {
      return await axios.patch(
        process.env.API_IDENTITY + '/sign_up/check_for_open_user_invitation'
      );
      // return { status: 404 };
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 404, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async patchClaimOpenInvitation({ commit }, body) {
    try {
      const res = await axios.patch(
        process.env.API_IDENTITY + '/sign_up/claim_open_user_invitation',
        body
      );
      commit('setCurrentUser', res.data);
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async hasCarrierBeenClaimed(_, carrierId) {
    try {
      let res = null;
      if (carrierId) {
        const data = {
          carrier_id: carrierId,
        };
        res = await axios.get(
          process.env.API_IDENTITY + '/sign_up/has_carrier_been_claimed',
          {
            params: data,
          }
        );
      } else {
        res = await axios.get(
          process.env.API_IDENTITY + '/sign_up/has_carrier_been_claimed'
        );
      }
      return res;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e?.response?.status === 404) {
          return { status: 404 };
        } else if ([403, 422].includes(e?.response?.status)) {
          return e.response;
        }
      } else {
        throw e;
      }
    }
  },

  async carrierAskToBeAdded({ commit }, body) {
    try {
      const res = await axios.post(
        process.env.API_IDENTITY + '/sign_up/carrier_ask_to_be_added',
        body
      );
      if ([200, 201].includes(res.status)) {
        if (res.data?.user) {
          commit('setCurrentUser', res.data.user);
        }
      }
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getCompanyUsers({ commit, state }, body) {
    if (!body.quiet) {
      commit('setCompanyUsersLoading', true);
    }

    const params = {
      page: body.page || '1',
      per_page: body.perPage || '10',
      'q[company_id_eq]': body.companyId,
      'q[active]': body.verifiedUsers || undefined,
      ...(body.query
        ? { 'q[email_or_first_name_or_last_name_cont]': body.query }
        : {}),
      ...(body.scopes && body.scopes.length
        ? { 'q[user_scopes_scope_in][]': body.scopes }
        : {}),
    };
    try {
      const res = await axios.get(process.env.API_IDENTITY + '/api/v1/users', {
        params,
      });

      const mutation = body.verifiedUsers
        ? 'setVerifiedUsers'
        : 'setCompanyUsers';

      if (body.filtered) {
        const filtered_data = uniqBy(res.data, 'id');

        commit(mutation, filtered_data);
        commit('setPagination', res.headers);
      } else {
        if (body.refetch) {
          const combinedUsers = [...(state.companyUsers || []), ...res.data];

          commit(mutation, combinedUsers);
          commit('setPagination', res.headers);
        } else {
          commit(mutation, res.data);
          commit('setPagination', res.headers);
        }
      }

      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setCompanyUsersLoading', false);
    }
  },

  async userSignOut({ commit }) {
    try {
      const res = await axios.get(process.env.API_IDENTITY + '/sign_out');
      if ([204].includes(res?.status)) {
        // let noUser = {
        //   id: null,
        //   first_name: null,
        //   last_name: null,
        //   company: {
        //     id: null,
        //     status: null,
        //   },
        // };
        commit('setCurrentUser', null);
        localStorage.clear();
        // return something so user still gets redirected to sign-up page
        return true;
      }
      return res;
    } catch (e) {
      if ([401].includes(e.response?.status)) {
        // let noUser = {
        //   id: null,
        //   first_name: null,
        //   last_name: null,
        //   company: {
        //     id: null,
        //     status: null,
        //   },
        // };
        commit('setCurrentUser', null);
        localStorage.clear();
        // return something so user still gets redirected to sign-up page
        return e.response;
      } else if (
        axios.isAxiosError(e) &&
        [403, 422].includes(e.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },

  /**
   * This method is used to update the user profile account information
   * @param userId The user id of the user is going to be deleted.
   * @returns The API response with the status code and data.
   */
  async updateProfileInformation(_, { first_name, last_name, user_id }) {
    let formData = new FormData();
    formData.append('user[first_name]', first_name);
    formData.append('user[last_name]', last_name);
    try {
      let res = await axios.patch(
        process.env.API_IDENTITY + '/api/v1/users/' + user_id,
        formData
      );
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async setScopes(_, data) {
    console.log('Received data is:', data);
    try {
      const res = await axios.patch(
        `${process.env.API_IDENTITY}/api/v1/users/${data.id}`,
        data.scopes
      );
      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async deleteUser({ commit, state }, id) {
    try {
      const res = await axios.delete(
        process.env.API_IDENTITY + '/api/v1/users/' + id
      );
      if ([200, 201, 204].includes(res.status)) {
        let newState = state.companyUsers.filter((el) => el.id !== id);
        commit('setCompanyUsers', newState);
      }

      return res;
    } catch (e) {
      if (axios.isAxiosError(e) && [401].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getFeatureFlags({ commit }, user) {
    if (!user?.id) return;

    try {
      const requests = [
        axios.get(
          `${process.env.API_IDENTITY}/api/flipper/actors/User;${user.id}`
        ),
      ];

      if (user?.company?.id) {
        requests.push(
          axios.get(
            `${process.env.API_IDENTITY}/api/flipper/actors/Company;${user.company.id}`
          )
        );
      }

      const [userRes, companyRes] = await Promise.all(requests);
      const userFlags = userRes.data.features || {};
      const companyFlags = companyRes?.data?.features || {};

      const mergedFlags = {};

      const allKeys = new Set([
        ...Object.keys(userFlags),
        ...Object.keys(companyFlags),
      ]);

      for (const key of allKeys) {
        const userVal = userFlags[key]?.enabled ?? false;
        const companyVal = companyFlags[key]?.enabled ?? false;
        mergedFlags[key] = { enabled: companyVal || userVal };
      }

      commit('setFeatureFlags', mergedFlags);
    } catch (e) {
      if (axios.isAxiosError(e) && [401].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async updatePreferredName({ commit }, preferred_name) {
    try {
      let res = await axios.patch(
        `${process.env.API_MONITOR}/sign_up/details`,
        {
          preferred_name,
          idv_enforced: true,
        }
      );
      commit('setCurrentUser', res.data);
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [401, 403, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async updateEmail({ commit }, email) {
    try {
      let res = await axios.patch(
        `${process.env.API_MONITOR}/sign_up/details`,
        {
          email,
          idv_enforced: true,
        }
      );
      commit('setCurrentUser', res.data);
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [401, 403, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getUserInquiries({ commit, state }, userId) {
    if (!userId) return;
    if (state.currentUser?.last_verified_identity_at) {
      const msg =
        'From users.js: currentUser has non-null last_verified_identity_at value - bypassing getUserInquiries';
      commit('addFormDebugMessage', msg, { root: true });
      return;
    }
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/${userId}/inquiries?q[s]=id desc&q[user_id_eq]=${userId}&q[is_type_not_eq]=ssn`
      );
      if (!res?.data || res.data?.length < 1) {
        commit('setInquiries', false);
      } else {
        commit('setInquiries', res.data);
      }
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 422, 401].includes(e?.response?.status)
      ) {
        console.warn('Error getting user inquiries', e?.response);
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async pollInquiries({ commit }, userId) {
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/${userId}/inquiries?q[s]=id desc&q[user_id_eq]=${userId}`
      );
      if (!res?.data || res.data?.length < 1) {
        commit('setInquiries', false);
      } else {
        commit('setInquiries', res.data);
      }
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [403, 422, 401].includes(e?.response?.status)
      ) {
        console.warn('Error getting user inquiries', e?.response);
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getInquiry({ state }) {
    const userId = state?.currentUser?.id;
    if (state?.currentUser?.last_verified_identity_at) {
      console.log('User has been verified, bypassing getInquiry');
      return;
    }
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/${userId}/inquiries/get_inquiry`
      );
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [401, 403, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getCDLInquiry({ state }) {
    const userId = state?.currentUser?.id;
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/${userId}/inquiries/get_inquiry/cdl`
      );
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [401, 403, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getSSNInquiry({ state }) {
    const userId = state?.currentUser?.id;
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/${userId}/inquiries/get_inquiry/ssn`
      );
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [401, 403, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getSelfieInquiry({ state }) {
    const userId = state?.currentUser?.id;
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/${userId}/inquiries/get_inquiry/selfie`
      );
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [401, 403, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async consentPrivacyPolicy({ commit }) {
    try {
      let res = await axios.patch(
        `${process.env.API_MONITOR}/consent_privacy_policy`
      );
      commit('setCurrentUser', res.data);
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [401, 403, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async checkLoadBoardAccess({ commit }) {
    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/users/check_my_load_board_access`
      );
      commit('setUserLoadBoardAccess', res.data);
      return res;
    } catch (e) {
      if (
        axios.isAxiosError(e) &&
        [401, 403, 422].includes(e?.response?.status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
};

// mutations
const mutations = {
  setCurrentUser(state, user) {
    state.currentUser = user;
    if (user && user?.id) {
      setGrafanaFaroUser(user);
      // Broadcast the active user ID to other tabs via localStorage.
      // This enables session conflict detection in the axios interceptor.
      localStorage.setItem('hw_active_user_id', String(user.id));
    } else {
      localStorage.removeItem('hw_active_user_id');
    }
  },
  setPagination(state, headers) {
    state.companyUsersPagination = {
      currentPage: headers.currentpage,
      totalPages: headers.totalpages,
      totalRecords: headers.totalitems,
    };
  },
  setRequestedCarrier(state, requested_carrier_id) {
    state.currentUser.requested_carrier_id = requested_carrier_id;
  },
  setCompanyUsers(state, user) {
    state.companyUsers = user;
  },
  setCompanyUsersLoading(state, bool) {
    state.isCompanyUsersLoading = bool;
  },
  removeCompanyUser(state, userId) {
    state.companyUsers = state.companyUsers.filter(
      (user) => user.id !== userId
    );
  },
  setPreferredCountry(state, value) {
    state.preferredCountry = value;
  },
  setIntercomIsBooted(state, bool) {
    state.intercomIsBooted = bool;
  },
  setIntercomIsUserLoggedIn(state, bool) {
    state.intercomIsUserLoggedIn = bool;
  },
  setFeatureFlags(state, features) {
    state.featureFlags = features;
  },
  setInquiries(state, inquiries) {
    state.inquiries = inquiries;
  },
  setVerifiedUsers(state, user) {
    state.verifiedUsers = user;
  },
  setUserLoadBoardAccess(state, data) {
    state.userLoadBoardAccess = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
